import { fn } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  PrismCheckbox,
  PrismComboSelectInput,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';
import dayjsFormat from 'lumen/helpers/dayjs-format';

export const CHANNEL_PLATFORM = 'channel_platform';

interface ParityParityManagerChannelRateFormSignature {
  Args: {
    back: unknown;
    currencyCode: unknown;
    issue: NewIssue;
    next: unknown;
    parentController: unknown;
    platformOptions: unknown;
    setIssueProperty: unknown;
    setIssuePropertyFromEvent: unknown;
    toggleValue: unknown;
  };
}

export default class ParityParityManagerChannelRateForm extends Component<ParityParityManagerChannelRateFormSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isChannelRateValid() {
    let isValidChannelRateByIssueView = Number(this.issueChannelRate) > 0;
    // unrestrict channel rate for b&s
    // @ts-expect-error: Incorrect type
    if (!this.args.parentController.isBaitAndSwitchView) {
      isValidChannelRateByIssueView &&=
        parseFloat(this.issueChannelRate ?? '') <
        parseFloat(this.issueBaseRate ?? '');
    }
    return (
      this.issueBaseRate &&
      this.issueChannelRate &&
      isValidChannelRateByIssueView
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    return this.issueChannel && this.isChannelRateValid;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issueBaseRate() {
    return this.args.issue?.base_rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issueChannel() {
    return this.args.issue?.channel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issueChannelRate() {
    return this.args.issue?.channel_rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleChannelsByIssueTypeValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.parentController.possibleChannels.find(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (option) => option.entity.id === this.args.issue.channel?.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPlatform() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.platformOptions.find(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (option) => option.id === this.args.issue[CHANNEL_PLATFORM],
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  handlePossibleChannelsByIssueTypeChange(option): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.args.setIssueProperty('channel', option.entity);
  }

  @action
  // @ts-expect-error: Incorrect type
  setSelectedPlatformId(option): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.args.setIssueProperty(CHANNEL_PLATFORM, option.id);
  }

  <template>
    <div class="flex flex-col p-500" id="add-issue-form">
      <h2 class="header-title pb-400">
        {{t "parity_manager.add_issue.channel_rate_info"}}
      </h2>
      <div class="flex w-full flex-col pb-400">
        <label class="input-label required-indicator">
          {{t "parity.channel"}}
        </label>
        <PrismComboSelectInput
          @isSearchable={{true}}
          @isSecondary={{true}}
          @onChange={{this.handlePossibleChannelsByIssueTypeChange}}
          {{! @glint-expect-error: Incorrect type }}
          @options={{@parentController.possibleChannels}}
          @placeholder={{t "parity.channel"}}
          @value={{this.possibleChannelsByIssueTypeValue}}
        />
      </div>

      <div class="grid grid-cols-2 gap-400">
        <PrismInput
          @errorMessage={{t "parity_manager.validation.channel_rate"}}
          @hasErrors={{not this.isChannelRateValid}}
          {{! @glint-expect-error: Incorrect type }}
          @inlineLabel={{@currencyCode}}
          @label={{t "parity.channel_rate"}}
          {{! @glint-expect-error: Incorrect type }}
          @onInput={{fn @setIssuePropertyFromEvent "channel_rate"}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{@issue.channel_rate}}
        />
        <div>
          <label class="input-label">{{t "rates.type"}}</label>
          <div class="checkbox flex items-center">
            <PrismCheckbox
              @checked={{@issue.channel_cancellation}}
              {{! @glint-expect-error: Incorrect type }}
              @onChange={{fn @toggleValue "channel_cancellation"}}
            >
              {{t "general.cancellable"}}
            </PrismCheckbox>
          </div>
        </div>
      </div>

      <div class="flex w-full flex-col">
        <PrismInput
          @label={{t "parity_manager.channel_room_name"}}
          {{! @glint-expect-error: Incorrect type }}
          @onInput={{fn @setIssuePropertyFromEvent "channel_room_name"}}
          @value={{@issue.channel_room_name}}
        />
      </div>

      <div class="grid grid-cols-2 gap-400">
        <div class="flex flex-col">
          <label class="input-label required-indicator">
            {{t "general.arrival_date"}}
          </label>
          <PrismInput
            @disabled={{true}}
            @type="text"
            {{! @glint-expect-error: Incorrect type }}
            @value={{dayjsFormat @issue.arrival_date format="L"}}
          />
        </div>
        <div class="flex flex-col">
          <label class="input-label required-indicator">
            {{t "rates.los"}}
          </label>
          <PrismInput @disabled={{true}} @type="text" @value={{@issue.los}} />
        </div>
      </div>

      {{! @glint-expect-error: Incorrect type }}
      {{#if @parentController.isMajorOtaView}}
        <div class="grid grid-cols-2 gap-400">
          <div class="flex flex-col">
            <label class="input-label required-indicator">
              {{t "general.platform.plural" count=1}}
            </label>
            <PrismComboSelectInput
              @isSecondary={{true}}
              @onChange={{this.setSelectedPlatformId}}
              {{! @glint-expect-error: Incorrect type }}
              @options={{@platformOptions}}
              @value={{this.selectedPlatform}}
            />
          </div>
        </div>
      {{/if}}
    </div>

    <div class="settings-modal__footer flex justify-end">
      <GeneralFormFieldsCancelAndConfirm
        {{! @glint-expect-error: Incorrect type }}
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        {{! @glint-expect-error: Incorrect type }}
        @confirm={{@next}}
        @confirmText={{t "general.next_step"}}
        @isConfirmDisabled={{not this.isFormValid}}
      />
    </div>
  </template>
}
